"use client";

import { LayoutGroup } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { useLearningJourney } from "@/hooks/journey/use-learning-journey";
import {
  useHandsOnControls,
  useHandsOnPiano,
} from "@/hooks/journey/use-hands-on-controls";
import { useStepBackShortcut } from "@/hooks/journey/use-step-back-shortcut";
import { repertoire as repertoireStore } from "@/lib/learning-journey/repertoire";
import type { JourneySnapshot, LearningJourney } from "@/lib/learning-journey/journey";
import type { SongLike } from "@/lib/learning-journey/phrase-plan";
import type { RangeRunner } from "@/lib/learning-journey/runtime/range-runner";
import type {
  Phrase,
  PhraseFSMSnapshot,
} from "@/lib/learning-journey/types";
import {
  applicableStepIndices,
  phraseStateToStepIndex,
  reachableStepIndices,
  stepIndexToPhraseState,
  stepPosition,
} from "@/lib/learning-journey/step-mapping";
import type { AnalysisChord } from "@/lib/music/analyzer-types";
import type { Song } from "@/lib/music/song";
import { SongControler } from "@/lib/track/controler/song-controler";
import { AccountPrompt } from "@/components/ui/organisms/account-prompt/account-prompt";
import { PracticeFeedbackPrompt } from "@/components/ui/organisms/practice-feedback-prompt/practice-feedback-prompt";
import { DebugOverlay } from "./debug-overlay";
import { extractIntroHarmonyInfo } from "./intro-harmony-info";
import { HandsOnHint } from "./shell/hands-on-hint";
import { FloatingHeader, FloatingShell, MiniSteps, PhraseChip } from "./shell";
import { ChainState } from "./states/chain-state";
import { ChordPresentation } from "./states/chord-presentation";
import { GripState } from "./states/grip-state";
import { BothHandsState } from "./states/both-hands-state";
import { PracticeState } from "./states/practice-state";
import { RhythmState } from "./states/rhythm-state";
import { SpeedupState } from "./states/speedup-state";

/**
 * Journey overlay for the piece tutorial. Rendered by `StudioPage` while
 * the studio is in `learn` mode (not a route), over the live piano-roll.
 * `songId` is the loaded piece's id, used to key the journey's progress.
 *
 * Responsibilities:
 *
 *  1. Resolve the loaded `Song` from the Studio track manager. The
 *     studio is already in `'learn'` mode (set by the entry point that
 *     mounts this overlay), so its own chrome (count-in, section-tips)
 *     stays masked and the piano-roll shows bare behind the panels.
 *  2. Mount the `LearningJourney` and route to the relevant state view
 *     based on the current FSM snapshot. Phase 2/3 and rhythm states
 *     (S4/S5) are placeholders, see `chantier 8c/8d`.
 *
 * The dev-only `DebugOverlay` is intentionally still rendered so we
 * can iterate on transitions without driving the whole journey from
 * keyboard input.
 *
 * Cleanup policy: we do **not** unload the song from the Studio on
 * unmount. The user may navigate back to `/studio` and expect to keep
 * working on the same piece (decision §8.17 of the plan).
 */
export function LearnPiecePage({
  songId,
  pieceId,
}: {
  songId: string;
  /**
   * Catalogue identity, the only key that can load this piece back. `null`
   * for anything that did not come through a catalogue entry, in which case
   * the piece plays and progresses normally but never joins the repertoire
   * queue — an offer that cannot open anything is worse than no offer.
   */
  pieceId: string | null;
}) {
  const { studio } = useStudio();

  // The S4/S5/phase-2 runs slow the transport to 60% (or another tier).
  // The `RangeRunner` deliberately does not restore the rate between runs
  //, they typically share a tempo. But when the user navigates away from
  // `/learn-piece`, the `/studio` page would otherwise inherit the slowed
  // playback rate. We restore `1` on unmount so the Studio sounds nominal
  // again.
  useEffect(() => {
    if (studio === null) {
      return;
    }

    return () => {
      studio.getLocalTransport().setPlaybackRate(1);
    };
  }, [studio]);

  // Disable section tips for the whole learn-piece experience. The
  // `SectionTipsComponent` lives on the singleton Studio and stays
  // registered on the transport scheduler across route changes, so
  // sections (with tip content) loaded during a previous `/studio`
  // session leak into `/learn-piece`. There, the section-tips UI
  // (`StudioSectionTips`) is never mounted, so when the playhead crosses
  // such a boundary mid-run, `onSchedule` pauses + seeks the transport to
  // the section start but nothing ever calls `complete()` to resume: the
  // run is orphaned (roll frozen at the section start, status stuck on
  // `running`, free-running metronome still clicking). We turn tips off
  // while this page is mounted and restore them on unmount so `/studio`
  // keeps its interstitials.
  useEffect(() => {
    if (studio === null) {
      return;
    }

    studio.sectionTips.setEnabled(false);

    return () => {
      studio.sectionTips.setEnabled(true);
    };
  }, [studio]);

  // The player's own notes come off the roll for the whole journey.
  //
  // The live piano lane is cumulative and it is drawn in `--color-hand-live`,
  // which sits at **1.03:1 against `--color-hand-left`**: the same object in
  // luminance. Over a notation the player is reading, that is not feedback,
  // it is a second score in a colour that lies about whose it is — and it
  // accumulated pass over pass until the roll carried three performances at
  // once.
  //
  // It costs nothing outside the passes in time: the lane records only while
  // the transport plays, and S0 to S3 are all played paused, so there was
  // never anything to draw there in the first place. What it does take away
  // is the ghost after a pass — which comes back on demand, as the trace, on
  // the one screen that has room to read it.
  useEffect(() => {
    if (studio === null) {
      return;
    }

    studio.setLiveLaneMode("hidden");

    return () => {
      studio.setLiveLaneMode("notes");
    };
  }, [studio]);

  const song = useLoadedSong();
  const songLike = useMemo<SongLike | null>(() => toSongLike(song), [song]);

  const { snapshot, journey, rangeRunner } = useLearningJourney({
    song: songLike,
    morceauId: songId,
    // L'identité catalogue descend jusqu'à la mesure, où elle est la seule à
    // partir sous le nom `pieceId`. Elle était déjà ici, mais s'arrêtait au
    // répertoire : les sessions de pratique portaient alors la clé de
    // progression sous ce nom-là, et aucune ne se joignait à `piece_opened`.
    pieceId,
  });

  // Le morceau sur lequel on est, pour l'écran vide du studio.
  //
  // Ici et pas à la première note : ouvrir le parcours **est** l'acte que la
  // reprise propose de refaire, et un signal plus fin obligerait le parcours
  // à rapporter chacune de ses écritures pour une ligne qui ne nomme jamais
  // qu'un morceau. Sans identité de catalogue on n'écrit rien : une offre
  // qui n'ouvre rien est pire que pas d'offre.
  const title = song?.getTitle() ?? "";

  useEffect(() => {
    if (pieceId === null || title === "") {
      return;
    }

    repertoireStore().working(
      { morceauId: songId, pieceId, title },
      Date.now(),
    );
  }, [songId, pieceId, title]);

  // Pause + scroll to the new phrase start whenever the FSM advances
  // past a DONE phrase (or `goToPhrase` retargets the journey). Without
  // this, a still-running transport (typical after a successful S5 chain
  // run that played all the way through) would keep rolling past the new
  // phrase and the user would land mid-bar. We only react to
  // *transitions*, not to the initial mount (avoids a phantom pause on
  // first paint).
  //
  // S0 plays the phrase on arrival and re-centres the roll itself when
  // that playback ends (see `use-s0-walkthrough`), so the two do not
  // fight: this effect fires on the phrase change, that one after audio.
  const prevPhraseIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (studio === null || snapshot === null) {
      return;
    }

    const idx = snapshot.currentPhraseIndex;
    const prev = prevPhraseIndexRef.current;
    prevPhraseIndexRef.current = idx;

    if (prev === null || prev === idx) {
      return;
    }

    const phrase = snapshot.currentPhrase;

    if (phrase === null) {
      return;
    }

    studio.getLocalTransport().detach();
    void studio.animateScrollToTick(phrase.startTick);
  }, [studio, snapshot]);

  const songRuntimeInfo = useMemo(() => deriveSongRuntimeInfo(song), [song]);
  const songControler = useSongControler();

  if (studio === null) {
    return <CenterMessage label="Initialisation du Studio..." />;
  }

  if (song === null) {
    return (
      <CenterMessage label="Aucun morceau chargé. Importe un MIDI depuis /studio puis reviens ici." />
    );
  }

  if (snapshot === null || journey === null || songRuntimeInfo === null) {
    return <CenterMessage label="Préparation du parcours..." />;
  }

  const phraseTotal = snapshot.phraseProgress.length;
  // Shown from the moment there are units to name, and not only while the
  // parcours is on screen: after phase 1 the chip is the only thing that
  // says which passage the roll is on, and the only way back into a unit.
  const showPhraseChip = phraseTotal > 0;

  return (
    <>
      {showPhraseChip && (
        <PhraseChip snapshot={snapshot} journey={journey} />
      )}
      <LayoutGroup>
        <CurrentStateView
          song={song}
          songId={songId}
          pieceId={pieceId}
          snapshot={snapshot}
          journey={journey}
          chords={songRuntimeInfo.chords}
          bpm={songRuntimeInfo.bpm}
          ppq={songRuntimeInfo.ppq}
          ticksPerBeat={songRuntimeInfo.ticksPerBeat}
          ticksPerBar={songRuntimeInfo.ticksPerBar}
          rangeRunner={rangeRunner}
          songControler={songControler}
        />
      </LayoutGroup>
      {/*
        Frère de la vue d'étape, donc positionné dans les mêmes bornes que le
        reste de l'overlay — au-dessus des touches, à gauche de la minimap. Il
        décide lui-même s'il a quelque chose à montrer : la plupart du temps il
        ne rend rien.
      */}
      <PracticeFeedbackPrompt snapshot={snapshot} />
      {/*
        Même emplacement, même signal, et exclusive de la précédente : elle ne
        s'arme que si la capsule de retour était déjà consommée à l'ouverture
        de la session. Voir `account-prompt-rules` pour l'arbitrage.
      */}
      <AccountPrompt snapshot={snapshot} pieceTitle={title} />
      {process.env.NODE_ENV !== "production" && (
        <DebugOverlay snapshot={snapshot} journey={journey} songId={songId} />
      )}
    </>
  );
}

// ── State routing ────────────────────────────────────────────────

type CurrentStateViewProps = {
  readonly song: Song;
  readonly songId: string;
  /** Catalogue identity, or `null` for a piece the queue cannot load back. */
  readonly pieceId: string | null;
  readonly snapshot: JourneySnapshot;
  readonly journey: LearningJourney;
  readonly chords: readonly AnalysisChord[];
  readonly bpm: number;
  readonly ppq: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
  readonly rangeRunner: RangeRunner | null;
  readonly songControler: SongControler | null;
};

function CurrentStateView({
  song,
  songId,
  pieceId,
  snapshot,
  journey,
  chords,
  bpm,
  ppq,
  ticksPerBeat,
  ticksPerBar,
  rangeRunner,
  songControler,
}: CurrentStateViewProps) {
  // Memoised because it is a dependency of the pass's `onFinished`: a fresh
  // literal every render rebuilt that callback ~78 times during a median
  // traversal, once per bar, for a value that never changes.
  const keep = useMemo(
    () =>
      pieceId === null
        ? null
        : {
            morceauId: songId,
            pieceId,
            title: song.getTitle() ?? "",
          },
    [pieceId, songId, song],
  );

  // The piece screen serves both: climbing the ladder, and keeping the piece
  // once it is played through. Same rail, same map, same work list — what
  // changes is the top of the panel and the offer under it.
  //
  // It gives way to a unit's parcours when the player re-opened one
  // (`revisitUnit`): the piece is still what the phase is about, but it is
  // not what they are looking at.
  if (
    (snapshot.phase === "phase2" || snapshot.phase === "repertoire") &&
    snapshot.revisitingUnit === null
  ) {
    return (
      <SpeedupState
        journey={journey}
        rangeRunner={rangeRunner}
        songControler={songControler}
        phrases={journey.getPhrases()}
        sections={song.getSections()}
        bpm={bpm}
        ppq={ppq}
        ticksPerBeat={ticksPerBeat}
        ticksPerBar={ticksPerBar}
        repertoire={snapshot.phase === "repertoire"}
        keep={keep}
      />
    );
  }

  const phrase = snapshot.currentPhrase;
  const phraseSnapshot = snapshot.currentPhraseSnapshot;

  if (phrase === null || phraseSnapshot === null) {
    return null;
  }

  return (
    <PhraseStateView
      phrase={phrase}
      phraseSnapshot={phraseSnapshot}
      phraseLabel={`Phrase ${snapshot.currentPhraseIndex + 1}`}
      journey={journey}
      snapshot={snapshot}
      song={song}
      songId={songId}
      chords={chords}
      bpm={bpm}
      ppq={ppq}
      ticksPerBeat={ticksPerBeat}
      ticksPerBar={ticksPerBar}
      rangeRunner={rangeRunner}
      songControler={songControler}
    />
  );
}

type PhraseStateViewProps = {
  readonly phrase: Phrase;
  readonly phraseSnapshot: PhraseFSMSnapshot;
  readonly phraseLabel: string;
  readonly journey: LearningJourney;
  readonly snapshot: JourneySnapshot;
  readonly song: Song;
  readonly songId: string;
  readonly chords: readonly AnalysisChord[];
  readonly bpm: number;
  readonly ppq: number;
  readonly ticksPerBeat: number;
  readonly ticksPerBar: number;
  readonly rangeRunner: RangeRunner | null;
  readonly songControler: SongControler | null;
};

function PhraseStateView({
  phrase,
  phraseSnapshot,
  phraseLabel,
  journey,
  snapshot,
  song,
  songId,
  chords,
  bpm,
  ppq,
  ticksPerBeat,
  ticksPerBar,
  rangeRunner,
  songControler,
}: PhraseStateViewProps) {
  // S0 needs the tonality to make its degrees mean anything (`iv` and
  // `III` are relations to a key, not labels).
  const tonality = useMemo(
    () => extractIntroHarmonyInfo(song).tonality,
    [song],
  );

  // Real time for the phrase, so the audition can be a model rather than a
  // metronomic reading of the same notes.
  const tickToSeconds = useCallback(
    (tick: number) => song.getMusicalTimeline().tickToSeconds(tick),
    [song],
  );

  const handleAcknowledge = useCallback(
    () => journey.acknowledgePresentChord(),
    [journey],
  );

  const handleConfirmPosition = useCallback(
    () => journey.confirmPosition(),
    [journey],
  );

  const handlePracticeCompleted = useCallback(
    (outcome: "validated" | "failed", pace?: number) =>
      journey.completePracticeRun(outcome, pace),
    [journey],
  );

  useStepBackShortcut(phraseSnapshot.canStepBack, journey, rangeRunner);

  switch (phraseSnapshot.state) {
    case "S0":
      return (
        <ChordPresentation
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          journey={journey}
          chords={chords}
          phrases={journey.getPhrases()}
          phraseIndex={snapshot.currentPhraseIndex}
          tonality={tonality}
          ticksPerBeat={ppq}
          ticksPerBar={ticksPerBar}
          rangeRunner={rangeRunner}
          songId={songId}
          onAcknowledge={handleAcknowledge}
        />
      );

    case "S1a":
      return (
        <GripState
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          hand="left"
          chords={chords}
          journey={journey}
          onPositionConfirmed={handleConfirmPosition}
        />
      );

    case "S2a":
      return (
        <GripState
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          hand="right"
          chords={chords}
          journey={journey}
          onPositionConfirmed={handleConfirmPosition}
        />
      );

    case "S1b":
      return (
        <PracticeState
          key={`S1b:${phrase.startTick}`}
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          journey={journey}
          hand="left"
          chords={chords}
          tickToSeconds={tickToSeconds}
          phraseLabel={phraseLabel}
          onCompleted={handlePracticeCompleted}
        />
      );

    case "S2b":
      return (
        <PracticeState
          key={`S2b:${phrase.startTick}`}
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          journey={journey}
          hand="right"
          chords={chords}
          tickToSeconds={tickToSeconds}
          phraseLabel={phraseLabel}
          onCompleted={handlePracticeCompleted}
        />
      );

    case "S3":
      return (
        <BothHandsState
          key={`S3:${phrase.startTick}`}
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          journey={journey}
          tickToSeconds={tickToSeconds}
          bpm={bpm}
          ppq={ppq}
          phraseLabel={phraseLabel}
          onCompleted={handlePracticeCompleted}
        />
      );

    case "S4":
      return (
        <RhythmState
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          phraseLabel={phraseLabel}
          journey={journey}
          rangeRunner={rangeRunner}
          songControler={songControler}
          bpm={bpm}
          ppq={ppq}
          ticksPerBeat={ticksPerBeat}
          ticksPerBar={ticksPerBar}
        />
      );

    case "S5":
      return (
        <ChainState
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          phraseLabel={phraseLabel}
          journey={journey}
          snapshot={snapshot}
          rangeRunner={rangeRunner}
          songControler={songControler}
          bpm={bpm}
          ppq={ppq}
          ticksPerBeat={ticksPerBeat}
          ticksPerBar={ticksPerBar}
          phrases={journey.getPhrases()}
        />
      );

    case "DONE":
      return (
        <DonePhrasePanel
          phrase={phrase}
          phraseSnapshot={phraseSnapshot}
          phraseLabel={phraseLabel}
          journey={journey}
          snapshot={snapshot}
        />
      );
  }
}

const DONE_PANEL_WIDTH_PX = 340;

/**
 * The panel a validated unit shows.
 *
 * It is reached two ways, and they do not want the same thing. **Having just
 * finished the unit**, the motion is forward and the next phrase is the
 * primary act. **Having come back to it** — from the piece screen in a
 * visit, or from further along the parcours in phase 1 — the motion is
 * already spent: offering "phrase suivante" as the main button answers a
 * question they did not ask, one tap after they said which passage they
 * wanted. See `behind`, which is what tells the two apart in phase 1, where
 * a return carries no flag of its own.
 *
 * Everything on it is about **this** unit, and it has to say so. "Refaire
 * depuis le début" reads as the beginning of the *piece* to someone who has
 * just crossed the whole thing, and the fear it produces — that re-opening a
 * phrase throws the piece away — is enough to make the panel unusable. The
 * unit is named in the kicker and in the buttons, nowhere implied.
 */
function DonePhrasePanel({
  phrase,
  phraseSnapshot,
  phraseLabel,
  journey,
  snapshot,
}: {
  phrase: Phrase;
  phraseSnapshot: PhraseFSMSnapshot;
  phraseLabel: string;
  journey: LearningJourney;
  snapshot: JourneySnapshot;
}) {
  const titleId = `done-phrase-${snapshot.currentPhraseIndex}`;
  const totalPhrases = snapshot.phraseProgress.length;
  /**
   * The unit as the buttons name it. Read off the same label as the kicker so
   * the two can never name different phrases, and named at all because
   * "cette phrase" is only unambiguous while the player remembers which one
   * they landed on: this panel is reached by scrolling to a passage, and by
   * walking off the unit before it.
   */
  const unit = phraseLabel.toLowerCase();
  const visiting = snapshot.revisitingUnit !== null;

  /**
   * The unit the player was actually working on: the first one not
   * validated. `-1` once the whole piece is through.
   */
  const front = snapshot.phraseProgress.findIndex((p) => p.etatMax !== "DONE");

  /**
   * They are standing on a unit they went **back** to.
   *
   * A visit is the phase-2 form of that and it is flagged, but phase 1 has
   * the same act with no flag on it: `revisitUnit` is a plain move of the
   * pointer while the parcours *is* the screen, so opening phrase 1 from the
   * chaining step of phrase 2 landed on this panel with « Phrase suivante »
   * as its filled button — the one act the player had just declined by
   * choosing a passage. What makes the screen a return is not which phase
   * the piece is in, it is that work is waiting further on.
   */
  const behind = front >= 0 && front > snapshot.currentPhraseIndex;
  const chosen = visiting || behind;
  // Moving on is phase 1's act. During a visit the roll and the chip are how
  // the player changes passage — they just used them — and a second way in
  // would be one too many.
  const hasNext = !visiting && snapshot.currentPhraseIndex + 1 < totalPhrases;
  const doneStepIndex = phraseStateToStepIndex("DONE");

  const handleRestart = useCallback(() => {
    journey.revisitPhraseState("S0");
  }, [journey]);

  const handleRhythm = useCallback(() => {
    journey.revisitPhraseState("S4");
  }, [journey]);

  // Where they were, when they came back from further on: the unit still to
  // be worked, and not merely the one after this. « Phrase suivante » from
  // phrase 1 of a piece worked up to phrase 4 names a passage nobody left.
  const forwardIndex = behind ? front : snapshot.currentPhraseIndex + 1;

  const handleNext = useCallback(() => {
    journey.goToPhrase(forwardIndex);
  }, [journey, forwardIndex]);

  // The stepper is clickable here as it is everywhere else in the parcours.
  // It is the only way to open the chaining step on a unit that is already
  // through, and the two buttons above are shortcuts to two of its rungs.
  const reachable = useMemo(
    () =>
      reachableStepIndices(
        phrase,
        phraseSnapshot.maxState,
        phraseSnapshot.chains,
      ),
    [phrase, phraseSnapshot.maxState, phraseSnapshot.chains],
  );
  // What this unit's stepper counts: the steps it actually has. A
  // right-hand-only unit has three, not five with two dead slots.
  const steps = useMemo(
    () => applicableStepIndices(phrase, phraseSnapshot.chains),
    [phrase, phraseSnapshot.chains],
  );
  const handleJump = useCallback(
    (idx: number) => {
      const target = stepIndexToPhraseState(idx);

      if (target === null) {
        return;
      }

      journey.revisitPhraseState(target);
    },
    [journey],
  );

  const handsOn = useHandsOnPiano();

  /**
   * The foot takes the button of the screen, whichever it is.
   *
   * It is the same rule as everywhere — the pedal does what the player was
   * going to do anyway — and here that depends on why they are on this
   * panel. In phase 1 it is the next phrase. **In a visit it is redoing this
   * one**, which the pedal is otherwise never given: "redoing from scratch"
   * is on the list of things a foot must not trigger by accident. It is not
   * an accident here. The player crossed the piece, scrolled to a passage,
   * and asked to train on it; re-opening its five steps is the act they came
   * for, and it costs them nothing — the unit keeps every step it has
   * unlocked, and no other unit moves.
   */
  const footAction = chosen ? handleRestart : hasNext ? handleNext : null;

  useHandsOnControls({
    enabled: footAction !== null,
    onAdvance: footAction,
  });

  return (
    <FloatingShell
      variant="floating"
      width={DONE_PANEL_WIDTH_PX}
      ariaLabel="Phrase validée"
      titleId={titleId}
      contentKey={`done-${snapshot.currentPhraseIndex}`}
    >
      <FloatingHeader
        stepIndex={stepPosition(steps, doneStepIndex)}
        stepTotal={steps.length}
        kicker={`${phraseLabel.toUpperCase()} · VALIDÉE`}
        titleId={titleId}
      />

      <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 10 }}>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--color-text-secondary)",
            textWrap: "pretty",
          }}
        >
          Tu peux la refaire en entier, ou reprendre une seule de ses étapes.
          Le reste du morceau ne bouge pas.
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
          {/*
            The same two acts in both cases; which one is the button of the
            screen is what changes. In a visit the player has already chosen
            the passage, so redoing it is what they came for.
          */}
          {chosen ? (
            <DonePrimaryButton onClick={handleRestart}>
              {`Refaire la ${unit} en entier`}
            </DonePrimaryButton>
          ) : (
            <DoneSecondaryButton onClick={handleRestart}>
              {`Refaire la ${unit} en entier`}
            </DoneSecondaryButton>
          )}

          <DoneSecondaryButton onClick={handleRhythm}>
            {`Refaire la ${unit} en rythme`}
          </DoneSecondaryButton>

          {hasNext &&
            (behind ? (
              <DoneSecondaryButton onClick={handleNext}>
                {`Reprendre la phrase ${forwardIndex + 1} →`}
              </DoneSecondaryButton>
            ) : (
              <DonePrimaryButton onClick={handleNext}>
                Phrase suivante →
              </DonePrimaryButton>
            ))}
        </div>

        {handsOn && footAction !== null && (
          <HandsOnHint
            items={[
              {
                gesture: "Pédale",
                effect: chosen ? `refaire la ${unit}` : "phrase suivante",
              },
            ]}
          />
        )}
      </div>

      <MiniSteps
        active={doneStepIndex}
        steps={steps}
        reachable={reachable}
        onJump={handleJump}
      />
    </FloatingShell>
  );
}

function DoneSecondaryButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full transition-colors focus:outline-none focus-visible:ring-2"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 14px",
        borderRadius: 8,
        background: "transparent",
        border: "1px solid var(--color-border-subtle)",
        color: "var(--color-text-primary)",
        fontSize: 12,
        fontWeight: 500,
        cursor: "pointer",
        ["--tw-ring-color" as string]: "var(--color-accent)",
      }}
    >
      {children}
    </button>
  );
}

function DonePrimaryButton({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "10px 14px",
        borderRadius: 8,
        border: "none",
        background: "var(--color-accent)",
        color: "#ffffff",
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 0 22px var(--color-violet-glow)",
        cursor: "pointer",
        ["--tw-ring-color" as string]: "var(--color-violet-text)",
      }}
    >
      {children}
    </button>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Resolves the `Song` currently loaded in the Studio, by inspecting
 * the live tracks. Returns `null` while the Studio is bootstrapping or
 * when no song has been uploaded yet.
 *
 * Re-evaluated whenever the track manager notifies a change (track
 * added/removed) so the page picks up a freshly imported song without
 * a manual refresh.
 *
 * TODO V1.1, refactor to `useSyncExternalStore` over `TrackManager.onChange`.
 * The current `setState` inside `useEffect` triggers ESLint's
 * `react-hooks/set-state-in-effect` warning. Migrating requires a stable
 * snapshot reference (the same `Song` instance must come back across
 * `getSnapshot()` calls between two notifications), the track manager
 * already gives us that, but the refactor touches the parallel
 * `useSongControler` hook the same way, and we'd rather batch the change
 * with the broader `useStudio` modernization than slip it into chantier 11.
 */
function useLoadedSong(): Song | null {
  const { studio } = useStudio();
  const [song, setSong] = useState<Song | null>(null);

  useEffect(() => {
    if (studio === null) {
      setSong(null);

      return;
    }

    const trackManager = studio.getTrackManager();

    const refresh = () => {
      const next = findSongInTracks(studio);
      setSong(next);
    };

    refresh();

    return trackManager.onChange(refresh);
  }, [studio]);

  return song;
}

/**
 * Runtime info pulled from the loaded `Song` and used by the state
 * views (practice fluency threshold, rhythm count-in, burst detector
 * tick math, etc.):
 *
 *   - `bpm`        , tempo at the start of the song.
 *   - `chords`     , analyzer chords, used by the S0 view.
 *   - `ppq`        , pulses per quarter note (constant for a song),
 *                     consumed by the rhythm detectors.
 *   - `ticksPerBar`, measured at tick 0; the burst detector snaps the
 *                     rollback anchor to a bar boundary. Songs with
 *                     mid-piece time-signature changes will lose
 *                     accuracy here, acceptable in V1, calibration
 *                     belongs to chantier 11.
 */
function deriveSongRuntimeInfo(song: Song | null): {
  bpm: number;
  chords: AnalysisChord[];
  ppq: number;
  ticksPerBeat: number;
  ticksPerBar: number;
  numerator: number;
  denominator: number;
} | null {
  if (song === null) {
    return null;
  }

  const timeline = song.getMusicalTimeline();
  const bpm = timeline.getBpmAtTick(0);
  const signature = timeline.getTimeSignatureAtTick(0);

  return {
    bpm,
    chords: song.getAnalysisChords(),
    ppq: timeline.getPpq(),
    // The beat of the metre, not the quarter: 3/8 counts eighths. The run in
    // time rules its map with these, and a quarter-based ruler would draw
    // bars of the wrong length on the 9.4% of units in a compound metre.
    ticksPerBeat: timeline.getTicksPerBeat(0),
    ticksPerBar: timeline.getTicksPerBar(0),
    numerator: signature.numerator,
    denominator: signature.denominator,
  };
}

/**
 * Resolves the live `SongControler` instance currently loaded in the
 * Studio, mirroring `useLoadedSong`. The controler is needed by the
 * S4/S5 views to mute/unmute the song lanes during a rhythmic run
 * (`SongControler.setMuted`).
 */
function useSongControler(): SongControler | null {
  const { studio } = useStudio();
  const [controler, setControler] = useState<SongControler | null>(null);

  useEffect(() => {
    if (studio === null) {
      setControler(null);

      return;
    }

    const trackManager = studio.getTrackManager();

    const refresh = () => {
      setControler(findSongControlerInTracks(studio));
    };

    refresh();

    return trackManager.onChange(refresh);
  }, [studio]);

  return controler;
}

function findSongControlerInTracks(
  studio: ReturnType<typeof useStudio>["studio"],
): SongControler | null {
  if (studio === null) {
    return null;
  }

  for (const track of studio.getTrackManager().getTracks()) {
    const c = track.getControler();

    if (c instanceof SongControler) {
      return c;
    }
  }

  return null;
}

/**
 * Bridges the runtime `Song` to the `SongLike` shape the journey lib
 * expects. The friction is on `AnnotatedNote.note`: the lib types it
 * structurally as `{ readonly midi: number }`, but the real `Note`
 * keeps `midi` private and exposes it through `toMidi()`. The runtime
 * field is still readable (`private` is a TS-only modifier), so this
 * cast is sound at execution time. We isolate it here so consumer code
 * doesn't sprinkle `as unknown as SongLike` casts.
 */
function toSongLike(song: Song | null): SongLike | null {
  if (song === null) {
    return null;
  }

  return song as unknown as SongLike;
}

function findSongInTracks(studio: ReturnType<typeof useStudio>["studio"]): Song | null {
  if (studio === null) {
    return null;
  }

  for (const track of studio.getTrackManager().getTracks()) {
    const controler = track.getControler();

    if (controler instanceof SongControler) {
      return controler.getSong();
    }
  }

  return null;
}

function CenterMessage({ label }: { label: string }) {
  return (
    <div className="pointer-events-auto absolute inset-0 flex items-center justify-center">
      <p
        className="font-mono text-[11px] uppercase"
        style={{
          padding: "10px 16px",
          letterSpacing: "0.18em",
          color: "var(--color-text-secondary)",
          background: "var(--color-glass-shell)",
          backdropFilter: "blur(var(--blur-shell)) saturate(140%)",
          WebkitBackdropFilter: "blur(var(--blur-shell)) saturate(140%)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-shell)",
          boxShadow: "var(--shadow-popin-focus)",
        }}
      >
        {label}
      </p>
    </div>
  );
}


